import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { AiSummaryHealth, getAiSummaryHealthQuery } from 'api/aiSummaries';
import Loading from 'components/UI/Loading';
import styles from './AiSummaries.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const formatNumber = (value: number) => value.toLocaleString();
const formatCurrency = (value: number) => `$${value.toFixed(4)}`;
const formatCurrencyShort = (value: number) => `$${value.toFixed(2)}`;
const formatMonthLabel = (month: string) => dayjs(`${month}-01`).format('MMM YYYY');
const formatDate = (value?: string | null) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-');

const AiSummaries = () => {
    const [health, setHealth] = useState<AiSummaryHealth | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadHealth = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await getAiSummaryHealthQuery();
            setHealth(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to load AI summary health.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadHealth();
    }, []);

    if (isLoading && !health) return <Loading />;

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div>
                    <h1 className={styles.mainTitle}>AI Summaries</h1>
                    <p className={styles.titleSubtext}>Monitor newsletter summary volume, estimated cost, queue health, and failures. Daily windows use Eastern time.</p>
                </div>
                <button className={styles.refreshButton} onClick={loadHealth} type="button">
                    Refresh
                </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {health && (
                <>
                    <div className={styles.summaryGrid}>
                        <SummaryCard
                            label="Completed today (ET)"
                            value={formatNumber(health.totals.completedToday)}
                            subtext={`${formatNumber(health.totals.attemptsToday)} attempts today`}
                        />
                        <SummaryCard
                            label="Estimated cost today (ET)"
                            value={formatCurrency(health.totals.costToday)}
                            subtext={`${formatCurrency(health.totals.costMonth)} this month`}
                        />
                        <SummaryCard
                            label="Lifetime cost since rollout"
                            value={formatCurrencyShort(health.totals.costTotal)}
                            subtext={`${formatNumber(health.totals.completed)} completed all-time`}
                        />
                        <SummaryCard
                            label="Failure events since rollout"
                            value={formatNumber(health.totals.failureEvents)}
                            subtext={`${formatNumber(health.totals.failed)} currently failed`}
                        />
                    </div>

                    {health.monthlyBreakdown.length > 0 && (
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>Monthly Cost Breakdown (ET)</h2>
                            <div className={styles.chartContainer}>
                                <Bar
                                    data={{
                                        labels: [...health.monthlyBreakdown].reverse().map(m => formatMonthLabel(m.month)),
                                        datasets: [
                                            {
                                                label: 'Estimated cost (USD)',
                                                data: [...health.monthlyBreakdown].reverse().map(m => Number(m.cost.toFixed(4))),
                                                backgroundColor: 'rgba(246, 192, 53, 0.85)',
                                                borderColor: '#F6C035',
                                                borderWidth: 1,
                                            },
                                        ],
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { display: false },
                                            tooltip: {
                                                callbacks: {
                                                    label: ctx => `${formatCurrency(Number(ctx.parsed.y))}`,
                                                },
                                            },
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: { callback: value => `$${Number(value).toFixed(2)}` },
                                            },
                                            x: { grid: { display: false } },
                                        },
                                    }}
                                />
                            </div>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Completed</th>
                                        <th>Input tokens</th>
                                        <th>Output tokens</th>
                                        <th>Estimated cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {health.monthlyBreakdown.map(month => (
                                        <tr key={month.month}>
                                            <td>{formatMonthLabel(month.month)}</td>
                                            <td>{formatNumber(month.completed)}</td>
                                            <td>{formatNumber(month.inputTokens)}</td>
                                            <td>{formatNumber(month.outputTokens)}</td>
                                            <td>{formatCurrency(month.cost)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className={styles.sectionGrid}>
                        <MetricSection
                            title="Queue Backlog"
                            rows={['waiting', 'active', 'delayed', 'paused'].map(label => ({
                                label,
                                value: formatNumber(health.queue?.[label] || 0),
                            }))}
                        />
                        <MetricSection
                            title="Current Status Since Rollout"
                            rows={health.statusLast30Days.map(item => ({ label: item.status, value: formatNumber(item.count) }))}
                        />
                    </div>

                    <div className={styles.sectionGrid}>
                        <MetricSection
                            title="Skipped Reasons Since Rollout"
                            rows={health.skippedLast30Days.map(item => ({ label: item.reason, value: formatNumber(item.count) }))}
                        />
                        <MetricSection
                            title="Failure Reasons Since Rollout"
                            rows={health.failuresLast30Days.map(item => ({ label: item.reason, value: formatNumber(item.count) }))}
                        />
                    </div>

                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Daily Cost Last 14 Days (ET)</h2>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Completed</th>
                                    <th>Estimated cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {health.dailyLast14Days.map(day => (
                                    <tr key={day.date}>
                                        <td>{day.date}</td>
                                        <td>{formatNumber(day.completed)}</td>
                                        <td>{formatCurrency(day.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Top Users This Usage Period</h2>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Plan</th>
                                    <th>Summaries</th>
                                    <th>Cost</th>
                                    <th>Period</th>
                                </tr>
                            </thead>
                            <tbody>
                                {health.topUsers.map(user => (
                                    <tr key={`${user.userId}-${user.periodStart}`}>
                                        <td>{user.email}</td>
                                        <td>{user.planName || '-'}</td>
                                        <td>{formatNumber(user.summaryCount)}</td>
                                        <td>{formatCurrency(user.costUsdEstimate)}</td>
                                        <td>{dayjs(user.periodStart).format('MMM D')} - {dayjs(user.periodEnd).format('MMM D')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>Recent Failures</h2>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>User</th>
                                    <th>Email ID</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {health.recentFailures.map(failure => (
                                    <tr key={failure.summaryId}>
                                        <td>{formatDate(failure.failedAt || failure.updatedAt)}</td>
                                        <td>{failure.email || failure.userId}</td>
                                        <td>{failure.emailId}</td>
                                        <td>{failure.errorMessage || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const SummaryCard = ({ label, value, subtext }: { label: string; value: string; subtext: string }) => (
    <div className={styles.card}>
        <div className={styles.cardLabel}>{label}</div>
        <div className={styles.cardValue}>{value}</div>
        <div className={styles.cardSubtext}>{subtext}</div>
    </div>
);

const MetricSection = ({ title, rows }: { title: string; rows: Array<{ label: string; value: string }> }) => (
    <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {rows.length ? rows.map(row => (
            <div className={styles.metricRow} key={row.label}>
                <span className={styles.metricLabel}>{row.label}</span>
                <span className={styles.metricValue}>{row.value}</span>
            </div>
        )) : (
            <div className={styles.metricRow}>
                <span className={styles.metricLabel}>No data</span>
                <span className={styles.metricValue}>0</span>
            </div>
        )}
    </div>
);

export default AiSummaries;
